import type { AIModelConfig, AIModelKey, AIRates } from 'quadratic-shared/typesAndSchemasAI';

export const DEFAULT_MODEL_ROUTER_MODEL: AIModelKey = 'vertexai:gemini-2.5-flash-preview-05-20';

export const DEFAULT_MODEL: AIModelKey = 'bedrock-anthropic:claude:thinking-toggle-off';
export const DEFAULT_BACKUP_MODEL: AIModelKey = 'bedrock-anthropic:us.anthropic.claude-3-5-sonnet-20241022-v2:0';

export const DEFAULT_GET_CHAT_NAME_MODEL: AIModelKey = 'vertexai:gemini-2.5-flash-preview-05-20';

export const DEFAULT_CODE_EDITOR_COMPLETIONS_MODEL: AIModelKey = 'vertexai:gemini-2.5-flash-preview-05-20';

export const DEFAULT_GET_USER_PROMPT_SUGGESTIONS_MODEL: AIModelKey = 'vertexai:gemini-2.5-flash-preview-05-20';

export const DEFAULT_PDF_IMPORT_MODEL: AIModelKey = 'vertexai:gemini-2.5-pro-preview-05-06';

export const DEFAULT_SQL_MODEL: AIModelKey = 'bedrock-anthropic:us.anthropic.claude-3-7-sonnet-20250219-v1:0';
export const DEFAULT_SQL_MODEL_THINKING: AIModelKey =
  'bedrock-anthropic:us.anthropic.claude-3-7-sonnet-20250219-v1:0:thinking';

// updating this will force the model to be reset to the default model in local storage
export const DEFAULT_MODEL_VERSION = 16;

const claude_sonnet_3_5_20250514_rate: AIRates = {
  rate_per_million_input_tokens: 15,
  rate_per_million_output_tokens: 75,
  rate_per_million_cache_read_tokens: 1.5,
  rate_per_million_cache_write_tokens: 18.75,
};
const claude_sonnet_3_7_20250514_rate: AIRates = claude_sonnet_3_5_20250514_rate;
const claude_sonnet_4_20250514_rate: AIRates = claude_sonnet_3_5_20250514_rate;

const claude_opus_4_20250514_rate: AIRates = {
  rate_per_million_input_tokens: 15,
  rate_per_million_output_tokens: 75,
  rate_per_million_cache_read_tokens: 1.5,
  rate_per_million_cache_write_tokens: 18.75,
};

export const MODELS_CONFIGURATION: {
  [key in AIModelKey]: AIModelConfig;
} = {
  // uses 'DEFAULT_MODEL_ROUTER_MODEL' to find the best model for this prompt,
  // settings and price correspond to the current 'DEFAULT_MODEL_ROUTER_MODEL'
  'quadratic:quadratic-auto:thinking-toggle-off': {
    model: 'quadratic-auto',
    displayName: 'auto',
    temperature: 0,
    max_tokens: 65535,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'quadratic',
    promptCaching: false,
    thinkingToggle: false,
    rate_per_million_input_tokens: 0.15,
    rate_per_million_output_tokens: 1,
    rate_per_million_cache_read_tokens: 0,
    rate_per_million_cache_write_tokens: 0,
  },
  'quadratic:quadratic-auto:thinking-toggle-on': {
    model: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
    displayName: 'claude 3.7 sonnet',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'vertexai-anthropic:claude-opus-4:thinking-toggle-off': {
    model: 'claude-opus-4@20250514',
    displayName: 'claude opus 4',
    temperature: 0,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_opus_4_20250514_rate,
  },
  'vertexai-anthropic:claude-opus-4:thinking-toggle-on': {
    model: 'claude-opus-4@20250514',
    displayName: 'claude opus 4',
    temperature: 1,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_opus_4_20250514_rate,
  },
  'vertexai-anthropic:claude-sonnet-4:thinking-toggle-off': {
    model: 'claude-sonnet-4@20250514',
    displayName: 'claude sonnet 4',
    temperature: 0,
    max_tokens: 64000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_sonnet_4_20250514_rate,
  },
  'vertexai-anthropic:claude-sonnet-4:thinking-toggle-on': {
    model: 'claude-sonnet-4@20250514',
    displayName: 'claude sonnet 4',
    temperature: 1,
    max_tokens: 64000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_4_20250514_rate,
  },
  'vertexai-anthropic:claude:thinking-toggle-off': {
    model: 'claude-3-5-sonnet-v2@20241022',
    displayName: 'claude',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_sonnet_3_5_20250514_rate,
  },
  'vertexai-anthropic:claude:thinking-toggle-on': {
    model: 'claude-3-7-sonnet@20250219',
    displayName: 'claude',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'vertexai-anthropic:claude-3-7-sonnet@20250219': {
    model: 'claude-3-7-sonnet@20250219',
    displayName: 'claude 3.7 sonnet',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'vertexai-anthropic:claude-3-7-sonnet@20250219:thinking': {
    model: 'claude-3-7-sonnet@20250219',
    displayName: 'claude 3.7 sonnet thinking',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    thinking: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'vertexai-anthropic:claude-3-5-sonnet-v2@20241022': {
    model: 'claude-3-5-sonnet-v2@20241022',
    displayName: 'claude 3.5 sonnet',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai-anthropic',
    promptCaching: true,
    ...claude_sonnet_3_5_20250514_rate,
  },
  'vertexai:gemini-2.5-pro-preview-05-06': {
    model: 'gemini-2.5-pro-preview-05-06',
    displayName: 'gemini 2.5 pro preview',
    temperature: 0,
    max_tokens: 65535,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai',
    promptCaching: false,
    rate_per_million_input_tokens: 1.25,
    rate_per_million_output_tokens: 10,
    rate_per_million_cache_read_tokens: 0,
    rate_per_million_cache_write_tokens: 0,
  },
  'vertexai:gemini-2.5-flash-preview-05-20': {
    model: 'gemini-2.5-flash-preview-05-20',
    displayName: 'gemini 2.5 flash preview',
    temperature: 0,
    max_tokens: 65535,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'vertexai',
    promptCaching: false,
    rate_per_million_input_tokens: 0.15,
    rate_per_million_output_tokens: 1,
    rate_per_million_cache_read_tokens: 0,
    rate_per_million_cache_write_tokens: 0,
  },
  'bedrock-anthropic:claude-opus-4:thinking-toggle-off': {
    model: 'us.anthropic.claude-opus-4-20250514-v1:0',
    displayName: 'claude opus 4',
    temperature: 0,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_opus_4_20250514_rate,
  },
  'bedrock-anthropic:claude-opus-4:thinking-toggle-on': {
    model: 'us.anthropic.claude-opus-4-20250514-v1:0',
    displayName: 'claude opus 4',
    temperature: 1,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_opus_4_20250514_rate,
  },
  'bedrock-anthropic:claude-sonnet-4:thinking-toggle-off': {
    model: 'us.anthropic.claude-sonnet-4-20250514-v1:0',
    displayName: 'claude sonnet 4',
    temperature: 0,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_sonnet_4_20250514_rate,
  },
  'bedrock-anthropic:claude-sonnet-4:thinking-toggle-on': {
    model: 'us.anthropic.claude-sonnet-4-20250514-v1:0',
    displayName: 'claude sonnet 4',
    temperature: 1,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_4_20250514_rate,
  },
  'bedrock-anthropic:claude:thinking-toggle-off': {
    model: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0',
    displayName: 'claude',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: true,
    provider: 'bedrock-anthropic',
    promptCaching: false,
    thinkingToggle: false,
    ...claude_sonnet_3_5_20250514_rate,
  },
  'bedrock-anthropic:claude:thinking-toggle-on': {
    model: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
    displayName: 'claude',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: true,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'bedrock-anthropic:us.anthropic.claude-3-7-sonnet-20250219-v1:0': {
    model: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
    displayName: 'claude 3.7 sonnet',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'bedrock-anthropic:us.anthropic.claude-3-7-sonnet-20250219-v1:0:thinking': {
    model: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
    displayName: 'claude 3.7 sonnet thinking',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    thinking: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'bedrock-anthropic:us.anthropic.claude-3-5-sonnet-20241022-v2:0': {
    model: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0',
    displayName: 'claude 3.5 sonnet',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: false,
    ...claude_sonnet_3_5_20250514_rate,
  },
  'bedrock-anthropic:us.anthropic.claude-3-5-haiku-20241022-v1:0': {
    model: 'us.anthropic.claude-3-5-haiku-20241022-v1:0',
    displayName: 'claude 3.5 haiku',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'bedrock-anthropic',
    promptCaching: true,
    rate_per_million_input_tokens: 0.8,
    rate_per_million_output_tokens: 4,
    rate_per_million_cache_read_tokens: 0.08,
    rate_per_million_cache_write_tokens: 1,
  },
  'bedrock:us.deepseek.r1-v1:0': {
    model: 'us.deepseek.r1-v1:0',
    displayName: 'deepseek r1',
    temperature: 0,
    max_tokens: 32768,
    canStream: true,
    canStreamWithToolCalls: false,
    enabled: false,
    provider: 'bedrock',
    promptCaching: false,
    rate_per_million_input_tokens: 1.35,
    rate_per_million_output_tokens: 5.4,
    rate_per_million_cache_read_tokens: 0,
    rate_per_million_cache_write_tokens: 0,
  },
  'anthropic:claude-opus-4:thinking-toggle-off': {
    model: 'claude-opus-4-20250514',
    displayName: 'claude opus 4',
    temperature: 0,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_opus_4_20250514_rate,
  },
  'anthropic:claude-opus-4:thinking-toggle-on': {
    model: 'claude-opus-4-20250514',
    displayName: 'claude opus 4',
    temperature: 1,
    max_tokens: 32000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_opus_4_20250514_rate,
  },
  'anthropic:claude-sonnet-4:thinking-toggle-off': {
    model: 'claude-sonnet-4-20250514',
    displayName: 'claude sonnet 4',
    temperature: 0,
    max_tokens: 64000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_sonnet_4_20250514_rate,
  },
  'anthropic:claude-sonnet-4:thinking-toggle-on': {
    model: 'claude-sonnet-4-20250514',
    displayName: 'claude sonnet 4',
    temperature: 1,
    max_tokens: 64000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_4_20250514_rate,
  },
  'anthropic:claude:thinking-toggle-off': {
    model: 'claude-3-5-sonnet-20241022',
    displayName: 'claude',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinkingToggle: false,
    ...claude_sonnet_3_5_20250514_rate,
  },
  'anthropic:claude:thinking-toggle-on': {
    model: 'claude-3-7-sonnet-20250219',
    displayName: 'claude',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinking: true,
    thinkingToggle: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'anthropic:claude-3-7-sonnet-20250219': {
    model: 'claude-3-7-sonnet-20250219',
    displayName: 'claude 3.7 sonnet',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'anthropic:claude-3-7-sonnet-20250219:thinking': {
    model: 'claude-3-7-sonnet-20250219',
    displayName: 'claude 3.7 sonnet thinking',
    temperature: 1,
    max_tokens: 16000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'anthropic',
    promptCaching: true,
    thinking: true,
    ...claude_sonnet_3_7_20250514_rate,
  },
  'anthropic:claude-3-5-sonnet-20241022': {
    model: 'claude-3-5-sonnet-20241022',
    displayName: 'claude 3.5 sonnet',
    temperature: 0,
    max_tokens: 8192,
    canStream: true,
    canStreamWithToolCalls: true,
    promptCaching: true,
    enabled: false,
    provider: 'anthropic',
    ...claude_sonnet_3_5_20250514_rate,
  },
  'openai:gpt-4.1-2025-04-14': {
    model: 'gpt-4.1-2025-04-14',
    displayName: 'gpt 4.1',
    temperature: 0,
    max_tokens: 32768,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'openai',
    promptCaching: true, // not used for openai, managed by the api
    strictParams: false,
    rate_per_million_input_tokens: 2,
    rate_per_million_output_tokens: 8,
    rate_per_million_cache_read_tokens: 0.5,
    rate_per_million_cache_write_tokens: 0,
  },
  'openai:ft:gpt-4.1-mini-2025-04-14:quadratic::BZi7tAgl': {
    model: 'ft:gpt-4.1-mini-2025-04-14:quadratic::BZi7tAgl',
    displayName: 'gpt 4.1 mini fine-tuned (stable)',
    temperature: 0,
    max_tokens: 16384,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'openai',
    promptCaching: true, // not used for openai, managed by the api
    strictParams: false,
    rate_per_million_input_tokens: 0.8,
    rate_per_million_output_tokens: 3.2,
    rate_per_million_cache_read_tokens: 0.2,
    rate_per_million_cache_write_tokens: 0,
  },
  'openai:gpt-4.1-mini-2025-04-14': {
    model: 'gpt-4.1-mini-2025-04-14',
    displayName: 'gpt 4.1 mini',
    temperature: 0,
    max_tokens: 32768,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'openai',
    promptCaching: true, // not used for openai, managed by the api
    strictParams: false,
    rate_per_million_input_tokens: 0.4,
    rate_per_million_output_tokens: 1.6,
    rate_per_million_cache_read_tokens: 0.1,
    rate_per_million_cache_write_tokens: 0,
  },
  'openai:o4-mini-2025-04-16': {
    model: 'o4-mini-2025-04-16',
    displayName: 'o4 mini',
    temperature: 1, // only temperature 1 is supported for o1
    max_tokens: 100000,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'openai',
    promptCaching: true, // not used for openai, managed by the api
    strictParams: false,
    rate_per_million_input_tokens: 1.1,
    rate_per_million_output_tokens: 4.4,
    rate_per_million_cache_read_tokens: 0.275,
    rate_per_million_cache_write_tokens: 0,
  },
  'openai:o3-2025-04-16': {
    model: 'o3-2025-04-16',
    displayName: 'o3',
    temperature: 1, // only temperature 1 is supported for o1
    max_tokens: 100000,
    canStream: false, // stream is not supported for o1
    canStreamWithToolCalls: false,
    enabled: false,
    provider: 'openai',
    promptCaching: true, // not used for openai, managed by the api
    strictParams: false,
    rate_per_million_input_tokens: 10,
    rate_per_million_output_tokens: 40,
    rate_per_million_cache_read_tokens: 2.5,
    rate_per_million_cache_write_tokens: 0,
  },
  'xai:grok-3-beta': {
    model: 'grok-3-beta',
    displayName: 'grok 3 beta',
    temperature: 0,
    max_tokens: 131072,
    canStream: true,
    canStreamWithToolCalls: true,
    enabled: false,
    provider: 'xai',
    promptCaching: true, // not used for xai
    strictParams: false,
    rate_per_million_input_tokens: 3,
    rate_per_million_output_tokens: 15,
    rate_per_million_cache_read_tokens: 0,
    rate_per_million_cache_write_tokens: 0,
  },
} as const;
